// wayai use <hub> — bind this worktree to a specific hub (UUID or folder name).

import * as path from 'node:path';
import { writeBinding, readBinding } from '../lib/hub-binding.js';
import { detectWorkspace, findGitRoot, findHubByFolderName, scanWorkspaceHubs, scanNewHubs } from '../lib/workspace.js';
import { resolveLayout, hubsDirLabel } from '../lib/layout.js';
import { UUID_RE } from '../lib/utils.js';

export async function useCommand(args: string[]): Promise<void> {
  const target = args[0];
  if (!target || target.startsWith('-')) {
    console.error('Usage: wayai use <hub_uuid|folder-name>');
    process.exit(1);
  }

  let hubId: string;

  if (UUID_RE.test(target)) {
    hubId = target;
  } else {
    // Resolve folder name → hub_id from the hubs dir.
    const workspace = detectWorkspace();
    const gitRoot = findGitRoot();
    const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
    if (!workspaceDir) {
      console.error('Not inside a git repository, and no UUID provided.');
      process.exit(1);
    }
    const wsLabel = hubsDirLabel(gitRoot);

    const match = findHubByFolderName(workspaceDir, target);
    if (!match) {
      // A binding is a hub_id; an idless (uncreated) folder has none, so it can't
      // be bound yet. Point at the create path instead of the generic miss (gh #3117).
      const newHubs = scanNewHubs(workspaceDir);
      const pending = newHubs.find((h) => path.basename(h.hubFolder) === target);
      if (pending) {
        console.error(
          `"${target}" is a new hub that hasn't been created on the platform yet, so it has no id to bind to.\n` +
          `Create it first — the worktree binds automatically afterward:\n` +
          `  wayai create ${target}`,
        );
        process.exit(1);
      }

      console.error(`No hub matching "${target}" found in ${wsLabel}/. Pass a UUID or a folder name from:`);
      for (const h of scanWorkspaceHubs(workspaceDir)) {
        console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
      }
      for (const h of newHubs) {
        console.error(`  ${path.basename(h.hubFolder)} (new — "${h.hubName}", run \`wayai create ${path.basename(h.hubFolder)}\`)`);
      }
      process.exit(1);
    }
    hubId = match.hubId;
  }

  const previous = readBinding();
  if (previous === hubId) {
    console.log(`Worktree already bound to ${hubId}.`);
    return;
  }

  try {
    writeBinding(hubId);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }

  if (previous) {
    console.log(`Worktree rebound: ${previous} → ${hubId}`);
  } else {
    console.log(`Worktree bound to hub ${hubId}.`);
  }
}
